import { Fragment, useEffect, useState } from "react";
import ModalComponent from "../../../components/Tenant/ModalComponent/ModalComponent";
import { useGetUserActivityMutation } from "../../../features/tenant/userActivity/userActivityApi";

let DATE_FIELDS = ["dob"];
const KYC_FIELDS = [
    "aadhar",
    "pan",
    "gstin",
    "is_valid_aadhar",
    "is_valid_pan",
    "is_valid_gstin",
    "aadhar_details",
    "pan_details",
    "gstin_details",
    "aadhar_image",
    "pan_image",
    "gstin_image",
];
const HIDDEN_LOG_FIELDS = [
    "extra_status",
    "firm_name",
    "updated_by_name",
    "updated_by_mobile",
    // Internal KYC fields — logged in DB, hidden from panel UI
    "is_valid_aadhar",
    "is_valid_pan",
    "is_valid_gstin",
    "aadhar_details",
    "pan_details",
    "gstin_details",
    "aadhar_image",
    "pan_image",
    "gstin_image",
];
const MAX_CELL_PREVIEW = 50;

const FIELD_LABELS = {
    aadhar: "Aadhar",
    pan: "PAN",
    gstin: "GSTIN",
    is_valid_aadhar: "Valid Aadhar",
    is_valid_pan: "Valid PAN",
    is_valid_gstin: "Valid GSTIN",
    aadhar_details: "Aadhar Details",
    pan_details: "PAN Details",
    gstin_details: "GSTIN Details",
    aadhar_image: "Aadhar Image",
    pan_image: "PAN Image",
    gstin_image: "GSTIN Image",
};

const getSourceLabel = (updatedBy) => {
    const value = (updatedBy || "").toLowerCase();
    if (value === "self") return "App";
    if (value === "panel") return "Panel";
    return updatedBy
        ? updatedBy.charAt(0).toUpperCase() + updatedBy.slice(1)
        : "--";
};

const getPanelAdminName = (item) => item?.panel_admin_name || null;

const getPanelAdminRole = (item) =>
    item?.panel_admin_role || item?.panel_admin_designation || null;

export const UpdatedByCell = ({ item }) => {
    const [showAdminDetails, setShowAdminDetails] = useState(false);
    const updatedBy = (item?.updated_by || "").trim().toLowerCase();
    const adminName = getPanelAdminName(item);
    const adminRole = getPanelAdminRole(item);

    if (updatedBy === "self") {
        return <span>App</span>;
    }

    if (updatedBy === "panel") {
        return (
            <>
                <button
                    type="button"
                    className="text-blue-600 underline hover:text-blue-800"
                    onClick={() => setShowAdminDetails(true)}
                >
                    Panel
                </button>
                {showAdminDetails && (
                    <div
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
                        onClick={() => setShowAdminDetails(false)}
                    >
                        <div
                            className="bg-white rounded-lg p-4 max-w-sm w-full shadow-lg"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between mb-3">
                                <h5 className="font-semibold text-sm">
                                    Panel update
                                </h5>
                                <button
                                    type="button"
                                    className="text-gray-500 hover:text-gray-700 text-lg leading-none"
                                    onClick={() => setShowAdminDetails(false)}
                                >
                                    ×
                                </button>
                            </div>
                            <div className="mb-3">
                                <p className="text-xs text-gray-500 mb-1">
                                    Name
                                </p>
                                <p className="text-sm font-medium text-gray-900">
                                    {adminName || "--"}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 mb-1">
                                    Role
                                </p>
                                <p className="text-sm font-medium text-gray-900">
                                    {adminRole || "--"}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    }

    return <span>{getSourceLabel(item?.updated_by)}</span>;
};

const isKycField = (key) => KYC_FIELDS.includes(key);

const isEmptyActivityValue = (value) => {
    if (value === null || value === undefined || value === "") return true;
    if (typeof value === "string" && !value.trim()) return true;
    if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value) &&
        Object.keys(value).length === 0
    ) {
        return true;
    }
    if (Array.isArray(value) && value.length === 0) return true;
    return false;
};

const normalizeActivityValue = (value) => {
    if (isEmptyActivityValue(value)) return null;

    if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed) return null;

        if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
            try {
                return normalizeActivityValue(JSON.parse(trimmed));
            } catch (error) {
                return trimmed;
            }
        }

        return trimmed;
    }

    if (Array.isArray(value)) {
        return value.map((item) => normalizeActivityValue(item));
    }

    if (typeof value === "object" && value !== null) {
        const normalized = {};
        Object.keys(value)
            .sort()
            .forEach((key) => {
                normalized[key] = normalizeActivityValue(value[key]);
            });
        return normalized;
    }

    return value;
};

const areActivityValuesEqual = (a, b) => {
    if (a === b) return true;

    try {
        return (
            JSON.stringify(normalizeActivityValue(a)) ===
            JSON.stringify(normalizeActivityValue(b))
        );
    } catch (error) {
        return false;
    }
};

const getAllChangedEntries = (previousValues = {}, newValues = {}) =>
    Object.entries(previousValues).filter(
        ([key, prev]) => !areActivityValuesEqual(prev, newValues?.[key])
    );

const getChangedEntries = (previousValues = {}, newValues = {}) =>
    getAllChangedEntries(previousValues, newValues).filter(
        ([key]) => !HIDDEN_LOG_FIELDS.includes(key)
    );

const getVisibleEntries = (previousValues = {}, newValues = {}) =>
    getChangedEntries(previousValues, newValues);

const isKycLog = (previousValues = {}, newValues = {}) => {
    const keys = getAllChangedEntries(previousValues, newValues).map(
        ([key]) => key
    );
    return keys.some((key) => isKycField(key));
};

const isProfileLog = (previousValues = {}, newValues = {}) => {
    const keys = getAllChangedEntries(previousValues, newValues).map(
        ([key]) => key
    );
    return keys.length > 0 && keys.every((key) => !isKycField(key));
};

const formatActivityValue = (value, isDateField = false, fieldKey = "") => {
    if (value === null || value === undefined || value === "") return "--";

    if (fieldKey.startsWith("is_valid_") && typeof value === "boolean") {
        return String(value);
    }

    if (isDateField) {
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
    }

    if (typeof value === "object") {
        try {
            return JSON.stringify(value);
        } catch (error) {
            return String(value);
        }
    }

    return String(value);
};

const formatFieldLabel = (key) =>
    FIELD_LABELS[key] ||
    key
        ?.split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");

const ActivityValueCell = ({ value, isDateField, fieldKey, onViewFull }) => {
    const text = formatActivityValue(value, isDateField, fieldKey);

    if (text === "--") {
        return <span className="text-gray-400">--</span>;
    }

    if (text.length <= MAX_CELL_PREVIEW) {
        return <span className="break-words">{text}</span>;
    }

    return (
        <span className="inline-flex items-center gap-1 max-w-[220px]">
            <span className="truncate text-gray-700" title={text}>
                {text.slice(0, MAX_CELL_PREVIEW)}…
            </span>
            <button
                type="button"
                className="text-blue-600 text-xs underline shrink-0"
                onClick={() => onViewFull(text)}
            >
                View
            </button>
        </span>
    );
};

export const UserActivityModal = ({ showActivity, setShowActivity }) => {
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [activityTypeFilter, setActivityTypeFilter] = useState("all");
    const [fullValuePreview, setFullValuePreview] = useState(null);

    const [
        getUserActivity,
        {
            isLoading: userActivityLoading,
            error: userActivityError,
            data: userActivityData,
        },
    ] = useGetUserActivityMutation();

    const callApi = (filters) => {
        getUserActivity({
            token: sessionStorage.getItem("access_token"),
            tenant_id: JSON.parse(sessionStorage.getItem("vendorData"))
                .tenant_id,
            filters: {
                limit: 100,
                page: 1,
                ...filters,
            },
        });
    };

    const onSearch = (isClear = false) => {
        const nextStartDate = isClear ? "" : startDate;
        const nextEndDate = isClear ? "" : endDate;

        if (isClear) {
            setStartDate("");
            setEndDate("");
        }

        let updatedFilters = {
            ...showActivity,
            filters: {
                ...showActivity.filters,
                app_user_id: showActivity.filters?.app_user_id,
                start_date: nextStartDate,
                end_date: nextEndDate,
            },
        };

        setShowActivity(updatedFilters);
        callApi(updatedFilters?.filters);
    };

    useEffect(() => {
        if (showActivity.show === true && showActivity.filters?.app_user_id) {
            setStartDate("");
            setEndDate("");
            setActivityTypeFilter("all");
            callApi({
                app_user_id: showActivity.filters.app_user_id,
            });
        }
    }, [showActivity.show, showActivity.filters?.app_user_id]);

    const activityList = (userActivityData?.body?.list || []).filter((item) => {
        const changedEntries = getChangedEntries(
            item.previous_values,
            item.new_values
        );

        if (!changedEntries.length) {
            return false;
        }

        if (activityTypeFilter === "kyc") {
            return isKycLog(item.previous_values, item.new_values);
        }
        if (activityTypeFilter === "profile") {
            return isProfileLog(item.previous_values, item.new_values);
        }
        return true;
    });

    return (
        <ModalComponent
            size="6xl"
            show={showActivity?.show}
            setShow={() =>
                setShowActivity((prev) => ({ ...prev, show: false }))
            }
        >
            <div className="w-full p-4">
                <div className="border-b  border-gray-300 bg-gray-50 rounded-t-lg p-3">
                    <div className="flex  space-x-2 justify-between items-center">
                        <h4 className="text-xl font-semibold">User activity</h4>
                        <div className="d-flex justify-end gap-2">
                            <div className="w-52 h-10">
                                <div className="relative searchin">
                                    <label className="m-0 pl-1 text-sm text-left">
                                        Start date
                                    </label>
                                    <input
                                        type="date"
                                        id="default-search"
                                        className="w-full px-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                                        placeholder="Start Date"
                                        required
                                        value={startDate}
                                        onChange={(e) =>
                                            setStartDate(e.target.value)
                                        }
                                    />
                                </div>
                            </div>

                            <div className="w-52 h-10">
                                <div className="relative searchin">
                                    <label className="m-0 pl-1 text-sm text-left">
                                        End date
                                    </label>
                                    <input
                                        type="date"
                                        id="default-search"
                                        className="w-full px-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                                        placeholder="End Date"
                                        required
                                        value={endDate}
                                        onChange={(e) =>
                                            setEndDate(e.target.value)
                                        }
                                    />
                                </div>
                            </div>

                            <div className="flex flex-row items-end gap-2">
                                <div className="relative searchin">
                                    <button
                                        type="button"
                                        className="py-2 btn btn-outline-primary clear-both float-right  p-2"
                                        onClick={() => onSearch(false)}
                                    >
                                        Search
                                    </button>
                                </div>
                                <div className="relative searchin">
                                    <button
                                        className="btn btn-outline-primary clear-both float-right p-2 whitespace-nowrap"
                                        type="button"
                                        onClick={() => onSearch(true)}
                                    >
                                        Clear Filter
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex gap-2 mt-4">
                    {[
                        { key: "all", label: "All" },
                        { key: "profile", label: "Profile" },
                        { key: "kyc", label: "KYC" },
                    ].map(({ key, label }) => (
                        <button
                            key={key}
                            type="button"
                            className={`px-3 py-1.5 text-sm rounded-lg border ${
                                activityTypeFilter === key
                                    ? "bg-blue-700 text-white border-blue-700"
                                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                            }`}
                            onClick={() => setActivityTypeFilter(key)}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                <div className="table-responsive mt-4">
                    {userActivityLoading && (
                        <p className="text-sm text-gray-500">Loading activity...</p>
                    )}

                    {!userActivityLoading && activityList.length === 0 && (
                        <p className="text-sm text-gray-500">
                            No {activityTypeFilter === "all" ? "" : `${activityTypeFilter} `}
                            activity found.
                        </p>
                    )}

                    {activityList.length > 0 && (
                            <table className="table table-b">
                                <thead>
                                    <tr>
                                        <th className="bg-blue-700 text-center text-white">
                                            S.No.
                                        </th>

                                        <th className="bg-blue-700 text-white">
                                            Type
                                        </th>

                                        <th className="bg-blue-700 text-white">
                                            Field
                                        </th>

                                        <th className="bg-blue-700 text-white max-w-[240px]">
                                            Previous Value
                                        </th>

                                        <th className="bg-blue-700 text-white max-w-[240px]">
                                            New Value
                                        </th>

                                        <th className="bg-blue-700 text-center text-white">
                                            Updated by
                                        </th>

                                        <th className="bg-blue-700 text-white">
                                            Updated on
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {activityList.map((item, index) => {
                                            const {
                                                previous_values,
                                                new_values,
                                                created_at,
                                            } = item;
                                            const entries = getChangedEntries(
                                                previous_values,
                                                new_values
                                            );

                                            if (!entries.length) {
                                                return null;
                                            }

                                            const logType = isKycLog(
                                                previous_values,
                                                new_values
                                            )
                                                ? "KYC"
                                                : "Profile";

                                            const isOneRow =
                                                entries?.length === 1;

                                            const borderClass = {
                                                true: "border-b border-b-gray-400",
                                                false: "border-b border-b-gray-200",
                                            };

                                            const isDateField =
                                                DATE_FIELDS?.includes(
                                                    entries[0][0]
                                                );

                                            return (
                                                <Fragment key={index}>
                                                    <tr
                                                        className={
                                                            isOneRow
                                                                ? "border-b border-gray-200"
                                                                : ""
                                                        }
                                                    >
                                                        <td
                                                            rowSpan={
                                                                entries.length
                                                            }
                                                            className="bg-gray-100 text-center font-semibold border-b border-r border-gray-400"
                                                        >
                                                            {index + 1}
                                                        </td>

                                                        <td
                                                            rowSpan={
                                                                entries.length
                                                            }
                                                            className={`text-center text-xs font-semibold border-r border-gray-200 ${borderClass[isOneRow]}`}
                                                        >
                                                            <span
                                                                className={`inline-block px-2 py-0.5 rounded ${
                                                                    logType ===
                                                                    "KYC"
                                                                        ? "bg-purple-100 text-purple-700"
                                                                        : "bg-blue-100 text-blue-700"
                                                                }`}
                                                            >
                                                                {logType}
                                                            </span>
                                                        </td>

                                                        <td
                                                            className={`font-semibold border-r border-gray-200 ${borderClass[isOneRow]}`}
                                                        >
                                                            {formatFieldLabel(
                                                                entries[0][0]
                                                            )}
                                                        </td>
                                                        <td
                                                            className={`border-r border-gray-200 max-w-[240px] ${borderClass[isOneRow]}`}
                                                        >
                                                            <ActivityValueCell
                                                                value={
                                                                    entries[0][1]
                                                                }
                                                                isDateField={
                                                                    isDateField
                                                                }
                                                                fieldKey={
                                                                    entries[0][0]
                                                                }
                                                                onViewFull={
                                                                    setFullValuePreview
                                                                }
                                                            />
                                                        </td>
                                                        <td
                                                            className={`border-r border-gray-200 border-r-gray-400 max-w-[240px] ${borderClass[isOneRow]}`}
                                                        >
                                                            <ActivityValueCell
                                                                value={
                                                                    new_values[
                                                                        entries[0][0]
                                                                    ]
                                                                }
                                                                isDateField={
                                                                    isDateField
                                                                }
                                                                fieldKey={
                                                                    entries[0][0]
                                                                }
                                                                onViewFull={
                                                                    setFullValuePreview
                                                                }
                                                            />
                                                        </td>

                                                        <td
                                                            rowSpan={
                                                                entries.length
                                                            }
                                                            className="bg-gray-100 text-center border-b border-gray-400"
                                                        >
                                                            <UpdatedByCell
                                                                item={item}
                                                            />
                                                        </td>

                                                        <td
                                                            rowSpan={
                                                                entries.length
                                                            }
                                                            className="bg-gray-100 text-left border-b border-l border-gray-400"
                                                        >
                                                            {new Date(
                                                                created_at
                                                            ).toLocaleString()}
                                                        </td>
                                                    </tr>

                                                    {entries
                                                        .slice(1)
                                                        .map(
                                                            (
                                                                [key, prev],
                                                                i
                                                            ) => {
                                                                const isLast =
                                                                    i ===
                                                                    entries.slice(
                                                                        1
                                                                    ).length -
                                                                        1;

                                                                const isDateSubField =
                                                                    DATE_FIELDS?.includes(
                                                                        key
                                                                    );

                                                                return (
                                                                    <tr key={i}>
                                                                        <td
                                                                            className={`font-semibold border-r border-gray-200 ${borderClass[isLast]}`}
                                                                        >
                                                                            {formatFieldLabel(
                                                                                key
                                                                            )}
                                                                        </td>
                                                                        <td
                                                                            className={`border-r border-gray-200 max-w-[240px] ${borderClass[isLast]}`}
                                                                        >
                                                                            <ActivityValueCell
                                                                                value={
                                                                                    prev
                                                                                }
                                                                                isDateField={
                                                                                    isDateSubField
                                                                                }
                                                                                fieldKey={
                                                                                    key
                                                                                }
                                                                                onViewFull={
                                                                                    setFullValuePreview
                                                                                }
                                                                            />
                                                                        </td>
                                                                        <td
                                                                            className={`border-r border-gray-200 border-r-gray-400 max-w-[240px] ${borderClass[isLast]}`}
                                                                        >
                                                                            <ActivityValueCell
                                                                                value={
                                                                                    new_values[
                                                                                        key
                                                                                    ]
                                                                                }
                                                                                isDateField={
                                                                                    isDateSubField
                                                                                }
                                                                                fieldKey={
                                                                                    key
                                                                                }
                                                                                onViewFull={
                                                                                    setFullValuePreview
                                                                                }
                                                                            />
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            }
                                                        )}
                                                </Fragment>
                                            );
                                        }
                                    )}
                                </tbody>
                            </table>
                    )}
                </div>

                {fullValuePreview && (
                    <div
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
                        onClick={() => setFullValuePreview(null)}
                    >
                        <div
                            className="bg-white rounded-lg p-4 max-w-lg w-full shadow-lg"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <h5 className="font-semibold text-sm">
                                    Full value
                                </h5>
                                <button
                                    type="button"
                                    className="text-gray-500 hover:text-gray-700 text-lg leading-none"
                                    onClick={() => setFullValuePreview(null)}
                                >
                                    ×
                                </button>
                            </div>
                            <p className="text-sm break-all whitespace-pre-wrap bg-gray-50 border border-gray-200 rounded p-3 max-h-64 overflow-y-auto">
                                {fullValuePreview}
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </ModalComponent>
    );
};
